"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const models_1 = require("@osmosys/models");
const utils_1 = require("@osmosys/utils");
const constants_1 = require("../constants");
const isDirectOsysChild_1 = require("../utils/dom/isDirectOsysChild");
const findEls_1 = require("../utils/dom/findEls");
const hasAttr_1 = require("../utils/dom/hasAttr");
const getAttrOrDefault_1 = require("../utils/dom/getAttrOrDefault");
const getAttr_1 = require("../utils/dom/getAttr");
const getBlockType_1 = require("../utils/dom/getBlockType");
const domSetters_1 = require("../utils/dom/domSetters");
const elToHtmlString_1 = require("../utils/dom/elToHtmlString");
const generateId_1 = require("../utils/general/generateId");
const stringToDocument_1 = require("../utils/dom/stringToDocument");
const stringToEl_1 = require("../utils/dom/stringToEl");
const getElLogName_1 = require("../utils/general/getElLogName");
const getAncestorBlockArray_1 = require("../utils/general/getAncestorBlockArray");
const getBlockDisplayName_1 = require("../utils/general/getBlockDisplayName");
const social_1 = require("../social");
class BlueprintTemplateParser {
    // Log the initialize event
    constructor() {
        this.shareProviders = social_1.getShareProviders();
    }
    parseHtmlPartialString(html) {
        utils_1.logger.debug('BlueprintTemplateParser.parseHtmlPartialString');
        const el = stringToEl_1.stringToEl(html);
        const [blocks] = this.parseEl(el.content);
        utils_1.logger.debug(`BlueprintTemplateParser.parseHtmlPartialString | BLOCK COUNT: ${blocks.length}`);
        return blocks;
    }
    // Take an HTML string, search and find [osys] tags, and turn these tags into EmailTemplateBlock
    // and return the HTML string with [_osys:id]
    parseHtmlDocumentString(html) {
        utils_1.logger.debug('BlueprintTemplateParser.parse');
        const document = stringToDocument_1.stringToDocument(html);
        const [blocks, el] = this.parseEl(document);
        return [blocks, elToHtmlString_1.elToHtmlString(el)];
    }
    // Take a DOM element, search and find [osys] tags, and turn these tags into EmailTemplateBlock
    // and return the HTML string with [_osys:id]
    parseEl(containingEl, parent) {
        utils_1.logger.debug(`BlueprintTemplateParser.parseEl | PARSING ${getElLogName_1.getElLogName(containingEl)}...`);
        const ancestors = parent && parent.block ? getAncestorBlockArray_1.getAncestorBlockArray(parent.block) : [];
        const logPrefix = 'BlueprintTemplateParser.parseEl ' +
            (`[${!parent ? '' : getBlockDisplayName_1.getBlockDisplayName(parent.block)}${getElLogName_1.getElLogName(containingEl)}]` +
                ancestors.reduce((a, b) => `${a}--->[${getBlockDisplayName_1.getBlockDisplayName(b)}]`, '') +
                '---> |').toUpperCase();
        const elements = findEls_1.findEls(containingEl, 'osys');
        utils_1.logger.debug(`${logPrefix} LOOKING FOR ${parent ? `${ancestors.length + 1}x NESTED` : 'TOP-LEVEL'} BLOCKS`);
        // Only look for direct children
        const directChildEls = elements.filter(childEl => {
            return isDirectOsysChild_1.isDirectOsysChild(childEl, containingEl);
        });
        // If the parent is a repeater, and this doesn't have `[osys:repeat]` on it, filter it out
        // TODO: In the future, this could be shown but in some type of 'corrupted' or 'invalid' state in the UI
        const elsWithoutInvalidRepeats = directChildEls.filter(childEl => {
            if (parent &&
                parent.block &&
                parent.block.type === 'repeater' &&
                !hasAttr_1.hasAttr(childEl, 'repeat')) {
                return false;
            }
            return true;
        });
        // If the parent is a field and it is _only_ targetting the inner `contents`, filter it out, because the block would be wiped anyways
        // when it is rendered.
        // TODO: In the future, this could be shown but in some type of 'corrupted' or 'invalid' state in the UI
        const elsWithoutInvalidNestedChildren = elsWithoutInvalidRepeats.filter(() => {
            var _a, _b, _c;
            if (((_b = (_a = parent) === null || _a === void 0 ? void 0 : _a.block) === null || _b === void 0 ? void 0 : _b.type) === 'field' &&
                ((_c = parent.block.selectedTargets) === null || _c === void 0 ? void 0 : _c.filter(target => {
                    return constants_1.OSYS_CHILD_TARGETS.includes(target);
                }).length) === parent.block.selectedTargets.length) {
                return false;
            }
            return true;
        });
        // PARSE (READ) the DOM and create EmailTemplateBlocks based on that READ
        const blocks = elsWithoutInvalidNestedChildren.map(this.parseBlockWithinEl());
        // Return
        return [blocks, containingEl];
    }
    parseRepeaterDirectChild(parent, block) {
        return Object.assign(Object.assign({}, block), { repeatParentBlock: Object.assign(Object.assign({}, parent.block), { repeatChildBlocks: [
                    ...(block.repeatParentBlock &&
                        block.repeatParentBlock.repeatChildBlocks
                        ? block.repeatParentBlock.repeatChildBlocks
                        : []),
                    block,
                ] }) });
    }
    parseNonRepeaterDirectChild(parent, block) {
        return Object.assign(Object.assign({}, block), { parentBlock: Object.assign(Object.assign({}, parent.block), { childBlocks: [
                    ...(block.parentBlock && block.parentBlock.childBlocks
                        ? block.parentBlock.childBlocks
                        : []),
                    block,
                ] }) });
    }
    getBlockClientId(block) {
        return block.clientId || generateId_1.generateId();
    }
    parseBlockWithinEl(parent) {
        return (childEl, i) => {
            let block = new models_1.EmailTemplateBlock();
            // Basics:
            block.originalHtml = childEl.outerHTML;
            block.order = i;
            block.contentStr = '';
            block.type = getBlockType_1.getBlockType(childEl);
            // Take the name from osys:name="My name" or fallback to osys:field="My name" / osys:group="My name"...
            block.name = getAttrOrDefault_1.getAttrOrDefault(childEl, 'name', '');
            if (!block.name) {
                block.name = this.getNameFromAttr(block, childEl);
            }
            // Create a client ID and write it:
            block.clientId = this.getBlockClientId(block);
            domSetters_1.setAttr(childEl, 'cid', block.clientId); // TODO: Code smell right here (should not be writing data in a parser)
            // Populate parents' data:
            if (parent) {
                // If parsing the direct child of a repeater:
                if (parent.block.type === 'repeater') {
                    block = this.parseRepeaterDirectChild(parent, block);
                    // Otherwise:
                }
                else {
                    block = this.parseNonRepeaterDirectChild(parent, block);
                }
            }
            // Social share links
            // TODO: add parent group check
            if (block.type === 'social') {
                // TODO: Do some santization here
                block.socialProvider = getAttr_1.getAttr(childEl, 'social');
            }
            // Lockables
            block.lockable = hasAttr_1.hasAttr(childEl, 'lockable');
            block.locked = block.lockable && hasAttr_1.hasAttr(childEl, 'locked');
            // Hideables
            block.hideable = hasAttr_1.hasAttr(childEl, 'hideable');
            block.hidden = block.hideable ? hasAttr_1.hasAttr(childEl, 'hidden') : false;
            // Attributes
            block.attrs = this.getBlockElAttrs(childEl.attributes);
            block.attrKeys = Object.keys(block.attrs);
            // Field specific data:
            if (block.type === 'field') {
                block.fieldType = getAttrOrDefault_1.getAttrOrDefault(childEl, 'field:type', 'text');
                if (!block.fieldType || !models_1.FIELD_TYPES.includes(block.fieldType)) {
                    utils_1.logger.warn(`Block ${block.id} ${block.clientId} | Invalid field type: ${block.fieldType}. Setting fieldType to \`text\`...`);
                    block.fieldType = 'text';
                }
                // Set availableTargets and selectedTargets
                block.availableTargets = (getAttr_1.getAttr(childEl, 'field:target') ||
                    getAttr_1.getAttr(childEl, 'field:targets') ||
                    'contents').split(',');
                block.selectedTargets = block.availableTargets;
            }
            // Recurse through child blocks for 'fields' and for 'groups'
            if (block.type === 'field' || block.type === 'group') {
                const [childBlocks, blockEl] = this.parseEl(childEl, {
                    block,
                    cid: block.clientId,
                    el: childEl,
                    id: block.id,
                });
                const attrs = Array.from(childEl.attributes);
                const fieldChildBlocks = attrs
                    .filter(attrMeta => {
                    const attr = attrMeta.nodeName;
                    return /osys:field:\d+/.test(attr);
                })
                    .map(this.mapFieldChildBlocks(attrs, childEl))
                    // .map(b => {
                    //   if (block.type === 'field') {
                    //     return {
                    //       ...b,
                    //       clientId: block.clientId,
                    //     };
                    //   }
                    //   return b;
                    // })
                    .filter(cb => {
                    return !!cb;
                });
                // if (block.type === 'group') {
                block.childBlocks = [
                    ...fieldChildBlocks,
                    ...childBlocks,
                ];
                // } else {
                //   block.childBlocks = [...(fieldChildBlocks as EmailTemplateBlock[])];
                // }
                block.parsedHtml = elToHtmlString_1.elToHtmlString(blockEl);
            }
            else if (block.type === 'repeater') {
                const [repeatChildBlocks, blockEl] = this.parseEl(childEl, {
                    block,
                    cid: block.clientId,
                    el: childEl,
                    id: block.id,
                });
                block.repeatChildBlocks = repeatChildBlocks;
                // For repeaters, we want to clear out the HTML so that blocks can be repeated without bumping into the original blocks
                blockEl.innerHTML = '';
                block.parsedHtml = elToHtmlString_1.elToHtmlString(blockEl);
            }
            else {
                block.parsedHtml = elToHtmlString_1.elToHtmlString(childEl);
            }
            // Modify the Element, and save the parsed HTML onto the block
            block.el = childEl;
            // Return block
            return block;
        };
    }
    getNameFromAttr(block, childEl) {
        if (block.type === 'group') {
            return getAttrOrDefault_1.getAttrOrDefault(childEl, 'group', '');
        }
        else if (block.type === 'repeater') {
            return getAttrOrDefault_1.getAttrOrDefault(childEl, 'repeater', '');
        }
        else if (block.type === 'field') {
            return getAttrOrDefault_1.getAttrOrDefault(childEl, 'field', '');
        }
        else if (block.type === 'social') {
            if (block.socialProvider) {
                return social_1.getShareProviderPrettyName(block.socialProvider);
            }
            else {
                return 'Content item link (no social provider specified)';
            }
        }
        return '';
    }
    getBlockElAttrs(attributes) {
        return Array.from(attributes)
            .filter(a => {
            return a.name.startsWith('osys:');
        })
            .reduce((attrs, a) => {
            attrs[a.name || ''] = a.value || '';
            return attrs;
            // if (a.)
        }, {});
    }
    mapFieldChildBlocks(attrs, el) {
        return (attrMeta, i) => {
            const attr = attrMeta.nodeName;
            const matchedAttribute = attr.match(/osys:field:(\d+)$/);
            const id = matchedAttribute && matchedAttribute[1];
            if (!id) {
                return null;
            }
            const associatedAttriubtes = attrs.filter(nestedAttr => {
                return nestedAttr.nodeName.startsWith(`osys:field:${id}`);
            });
            const foundFieldType = associatedAttriubtes.find(reallyNestedAttr => {
                return (reallyNestedAttr.nodeName.endsWith('type') &&
                    reallyNestedAttr.nodeValue &&
                    models_1.FIELD_TYPES.includes(reallyNestedAttr.nodeValue));
            });
            let foundFieldName = associatedAttriubtes.find(reallyNestedAttr => {
                return (reallyNestedAttr.nodeName.endsWith('name') &&
                    reallyNestedAttr.nodeValue);
            });
            if (!foundFieldName) {
                foundFieldName = associatedAttriubtes.find(reallyNestedAttr => {
                    return (`osys:field:${id}` === reallyNestedAttr.nodeName &&
                        reallyNestedAttr.nodeValue);
                });
            }
            const foundFieldTarget = associatedAttriubtes.find(reallyNestedAttr => {
                return (reallyNestedAttr.nodeName.endsWith('target') &&
                    reallyNestedAttr.nodeValue);
            });
            const foundAvailableTargets = foundFieldTarget && foundFieldTarget.nodeValue
                ? foundFieldTarget.nodeValue.split(',')
                : ['inner'];
            // const attrKeys = associatedAttriubtes.map(ak => {
            //   return ak.nodeName;
            // });
            const childBlock = Object.assign(Object.assign({}, new models_1.EmailTemplateBlock()), { adContexts: [], attrKeys: associatedAttriubtes.map(ak => {
                    return ak.nodeName;
                }), attrs: this.getBlockElAttrs(el.attributes), availableTargets: foundAvailableTargets, conversions: [], fieldType: foundFieldType
                    ? foundFieldType.nodeValue
                    : 'text', name: foundFieldName && foundFieldName.nodeValue
                    ? foundFieldName.nodeValue
                    : '', order: i, selectedTargets: [foundAvailableTargets[0]], type: 'field', usingRichContentEditor: false });
            return childBlock;
        };
    }
}
exports.BlueprintTemplateParser = BlueprintTemplateParser;
//# sourceMappingURL=BlueprintTemplateParser.js.map